import VueCookies from "vue-cookies";
import CryptoJS from "crypto-js";

// ---------------------------------------------------------------------------
// Default cookie options (mirrors the Nuxt useEncryptedCookie defaults)
// ---------------------------------------------------------------------------
const defaultOptions = {
  expires: "1d",
  path: "/",
  secure: process.env.NODE_ENV === "production",
  sameSite: "Lax",
};

// ---------------------------------------------------------------------------
// Encryption helpers
// ---------------------------------------------------------------------------
const ENCRYPTED_PREFIX = "ENC:";

const secretKey = process.env.VUE_APP_ENCRYPTION_KEY;
const isEncryptionEnabled =
  typeof secretKey === "string" && secretKey.length > 0;

if (!isEncryptionEnabled && typeof window !== "undefined") {
  console.warn(
    "[cookieUtils] Encryption key (VUE_APP_ENCRYPTION_KEY) is missing. " +
      "Cookie values will be stored without encryption."
  );
}

/**
 * Try to parse a JSON string; return the raw value on failure.
 * @param {string} value
 * @returns {any}
 */
const tryParseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * AES-encrypt a value and prefix it with ENCRYPTED_PREFIX.
 * Non-string values are JSON-stringified first.
 * @param {any} value
 * @returns {string|null|undefined}
 */
const encryptValue = (value) => {
  if (value === null || value === undefined) return value;
  if (!isEncryptionEnabled)
    return typeof value === "string" ? value : JSON.stringify(value);

  const stringValue = typeof value === "string" ? value : JSON.stringify(value);

  return `${ENCRYPTED_PREFIX}${CryptoJS.AES.encrypt(
    stringValue,
    secretKey
  ).toString()}`;
};

/**
 * Decrypt a value that was encrypted by encryptValue.
 * Falls back gracefully if decryption fails or the value was never encrypted.
 * @param {any} value
 * @returns {any}
 */
const decryptValue = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "string") return value;

  if (!isEncryptionEnabled) return tryParseJson(value);

  if (!value.startsWith(ENCRYPTED_PREFIX)) {
    // Stored before encryption was enabled — return as-is (parsed if JSON)
    return tryParseJson(value);
  }

  try {
    const encrypted = value.slice(ENCRYPTED_PREFIX.length);
    const bytes = CryptoJS.AES.decrypt(encrypted, secretKey);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return tryParseJson(decrypted);
  } catch {
    // Decryption failed — return raw value to avoid crashes
    return tryParseJson(value);
  }
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export const cookieUtils = {
  /**
   * Set a cookie. The value is AES-encrypted before storage.
   *
   * @param {string} name
   * @param {any} value
   * @param {object} [options] - Overrides for defaultOptions
   */
  set(name, value, options = {}) {
    const opts = { ...defaultOptions, ...options };
    const encoded = encryptValue(value);

    VueCookies.set(
      name,
      encoded,
      opts.expires,
      opts.path,
      opts.domain,
      opts.secure,
      opts.sameSite
    );
  },

  /**
   * Get and decrypt a cookie value.
   *
   * @param {string} name
   * @returns {any|null}
   */
  get(name) {
    const raw = VueCookies.get(name);
    if (raw === null || raw === undefined) return null;
    return decryptValue(raw);
  },

  /**
   * Check whether a cookie exists.
   *
   * @param {string} name
   * @returns {boolean}
   */
  has(name) {
    return VueCookies.isKey(name);
  },

  /**
   * Remove a specific cookie.
   *
   * @param {string} name
   * @param {object} [options]
   */
  remove(name, options = {}) {
    const opts = { ...defaultOptions, ...options };
    VueCookies.remove(name, opts.path, opts.domain);
  },

  /**
   * Remove all cookies managed by this utility.
   *
   * @param {object} [options]
   */
  removeAll(options = {}) {
    const opts = { ...defaultOptions, ...options };
    VueCookies.keys().forEach((cookieName) => {
      VueCookies.remove(cookieName, opts.path, opts.domain);
    });
  },

  /**
   * Return all cookie names currently set.
   *
   * @returns {string[]}
   */
  getAllCookieNames() {
    return VueCookies.keys();
  },
};

export default cookieUtils;
